import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

const MONTH_LABEL_WIDTH = 40;
const ROW_HEIGHT = 28;

/**
 * Yearly metrics horizontal bar chart.
 * Value and carats use separate tracks + separate bottom scales so ticks match bar lengths.
 * @param {{ month: number, value: number, carats?: number|null }[]} points
 */
export default function DashboardYearlyChart({
  title,
  points = [],
  selectedMonth,
  formatValue,
  valueLegend,
  caratsLegend,
  emptyLabel,
  'data-testid': dataTestId,
}) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const isRtl = theme.direction === 'rtl';
  const locale = i18n?.language || undefined;

  const { maxValue, maxCarats, hasCarats, hasAnyValue, valueTicks, caratTicks } = useMemo(() => {
    let maxVal = 0;
    let maxCt = 0;
    let any = false;
    let caratsPresent = false;
    for (const p of points) {
      const value = Number(p.value) || 0;
      const carats = p.carats != null ? Number(p.carats) || 0 : null;
      if (value > 0 || (carats != null && carats > 0)) any = true;
      if (carats != null) caratsPresent = true;
      maxVal = Math.max(maxVal, value);
      if (carats != null) maxCt = Math.max(maxCt, carats);
    }
    const niceMaxVal = niceCeil(maxVal > 0 ? maxVal : 1);
    const niceMaxCt = niceCeil(maxCt > 0 ? maxCt : 1);
    return {
      maxValue: niceMaxVal,
      maxCarats: niceMaxCt,
      hasCarats: caratsPresent && maxCt > 0,
      hasAnyValue: any,
      valueTicks: buildTicks(niceMaxVal),
      caratTicks: buildTicks(niceMaxCt),
    };
  }, [points]);

  const monthLabels = useMemo(() => (
    Array.from({ length: 12 }, (_, i) => {
      const d = new Date(Date.UTC(2020, i, 1));
      return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(d);
    })
  ), [locale]);

  return (
    <Box sx={{ width: '100%', pt: 1.5, pe: 1.5 }} data-testid={dataTestId}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {title}
      </Typography>

      {!hasAnyValue ? (
        <Typography variant="caption" color="text.secondary">
          {emptyLabel}
        </Typography>
      ) : (
        <>
          <SeriesBlock
            isRtl={isRtl}
            points={points}
            selectedMonth={selectedMonth}
            monthLabels={monthLabels}
            getWidthPct={(p) => ((Number(p.value) || 0) / maxValue) * 100}
            getRaw={(p) => Number(p.value) || 0}
            formatTooltipValue={formatValue}
            barColorSelected="success.main"
            barColor="success.light"
            ticks={valueTicks}
            formatTick={formatCompactTick}
            legend={valueLegend}
            legendColor="success.main"
          />

          {hasCarats && (
            <Box sx={{ mt: 2 }}>
              <SeriesBlock
                isRtl={isRtl}
                points={points}
                selectedMonth={selectedMonth}
                monthLabels={monthLabels}
                getWidthPct={(p) => ((Number(p.carats) || 0) / maxCarats) * 100}
                getRaw={(p) => Number(p.carats) || 0}
                formatTooltipValue={(n) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} ct`}
                barColorSelected="secondary.main"
                barColor="secondary.light"
                ticks={caratTicks}
                formatTick={(n) => `${formatCompactTick(n)} ct`}
                legend={caratsLegend}
                legendColor="secondary.main"
                showMonthLabels={false}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function SeriesBlock({
  isRtl,
  points,
  selectedMonth,
  monthLabels,
  getWidthPct,
  getRaw,
  formatTooltipValue,
  barColorSelected,
  barColor,
  ticks,
  formatTick,
  legend,
  legendColor,
  showMonthLabels = true,
}) {
  return (
    <Box>
      <Box role="img" aria-label={legend} sx={{ width: '100%' }}>
        {points.map((p) => {
          const raw = getRaw(p);
          const pct = Math.max(0, Math.min(100, getWidthPct(p)));
          const isSelected = selectedMonth === p.month;
          return (
            <Box
              key={p.month}
              sx={{
                display: 'grid',
                gridTemplateColumns: isRtl
                  ? `minmax(0, 1fr) ${MONTH_LABEL_WIDTH}px`
                  : `${MONTH_LABEL_WIDTH}px minmax(0, 1fr)`,
                columnGap: 1,
                alignItems: 'center',
                height: ROW_HEIGHT,
                borderBottom: 1,
                borderColor: 'divider',
                boxSizing: 'border-box',
              }}
              title={[
                monthLabels[p.month - 1],
                formatTooltipValue ? formatTooltipValue(raw) : raw,
              ].filter(Boolean).join(' · ')}
            >
              {showMonthLabels ? (
                isRtl ? (
                  <>
                    <BarTrack pct={pct} selected={isSelected} color={barColorSelected} colorMuted={barColor} isRtl={isRtl} />
                    <MonthLabel label={monthLabels[p.month - 1]} selected={isSelected} isRtl={isRtl} />
                  </>
                ) : (
                  <>
                    <MonthLabel label={monthLabels[p.month - 1]} selected={isSelected} isRtl={isRtl} />
                    <BarTrack pct={pct} selected={isSelected} color={barColorSelected} colorMuted={barColor} isRtl={isRtl} />
                  </>
                )
              ) : (
                <>
                  {!isRtl && <Box />}
                  <BarTrack pct={pct} selected={isSelected} color={barColorSelected} colorMuted={barColor} isRtl={isRtl} />
                  {isRtl && <Box />}
                </>
              )}
            </Box>
          );
        })}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isRtl
              ? `minmax(0, 1fr) ${MONTH_LABEL_WIDTH}px`
              : `${MONTH_LABEL_WIDTH}px minmax(0, 1fr)`,
            columnGap: 1,
            alignItems: 'center',
            mt: 0.5,
          }}
        >
          {!isRtl && <Box />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: isRtl ? 'row-reverse' : 'row',
              justifyContent: 'space-between',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            {ticks.map((tick) => (
              <Typography
                key={tick}
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.6rem',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatTick(tick)}
              </Typography>
            ))}
          </Box>
          {isRtl && <Box />}
        </Box>
      </Box>

      {legend && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}
        >
          <Box
            component="span"
            sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: legendColor, display: 'inline-block' }}
          />
          {legend}
        </Typography>
      )}
    </Box>
  );
}

function MonthLabel({ label, selected, isRtl }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontSize: '0.7rem',
        lineHeight: 1,
        textAlign: isRtl ? 'left' : 'right',
        color: selected ? 'text.primary' : 'text.secondary',
        fontWeight: selected ? 700 : 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  );
}

function BarTrack({ pct, selected, color, colorMuted, isRtl }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: 10,
        display: 'flex',
        justifyContent: isRtl ? 'flex-end' : 'flex-start',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          height: 10,
          width: `${pct}%`,
          maxWidth: '100%',
          minWidth: pct > 0 ? 2 : 0,
          bgcolor: selected ? color : colorMuted,
          borderRadius: isRtl ? '3px 0 0 3px' : '0 3px 3px 0',
        }}
      />
    </Box>
  );
}

function buildTicks(niceMax) {
  const tickCount = 3;
  const step = niceMax / tickCount;
  return Array.from({ length: tickCount + 1 }, (_, i) => step * i);
}

function niceCeil(n) {
  if (!Number.isFinite(n) || n <= 0) return 1;
  const exp = Math.floor(Math.log10(n));
  const base = 10 ** exp;
  const fraction = n / base;
  let niceFraction;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * base;
}

function formatCompactTick(tick) {
  if (tick === 0) return '0';
  if (tick >= 1_000_000) return `${Math.round((tick / 1_000_000) * 10) / 10}M`;
  if (tick >= 1000) return `${Math.round(tick / 1000)}k`;
  return String(Math.round(tick * 100) / 100);
}
